import React from 'react';
import { cn } from '../../utils/cn';

interface GridProps {
  children: React.ReactNode;
  className?: string;
  cols?: {
    default?: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
  };
  gap?: 'sm' | 'md' | 'lg' | 'xl';
  align?: 'start' | 'center' | 'end' | 'stretch';
}

export const Grid: React.FC<GridProps> = ({ 
  children, 
  className,
  cols = { default: 1, md: 2, lg: 3 },
  gap = 'md',
  align = 'stretch'
}) => {
  const gapClasses = {
    sm: 'gap-4',
    md: 'gap-6',
    lg: 'gap-8',
    xl: 'gap-12'
  };

  const alignClasses = {
    start: 'items-start',
    center: 'items-center',
    end: 'items-end',
    stretch: 'items-stretch'
  };

  const defaultColsMap: Record<number, string> = {
    1: 'grid-cols-1', 2: 'grid-cols-2', 3: 'grid-cols-3', 4: 'grid-cols-4',
    5: 'grid-cols-5', 6: 'grid-cols-6', 12: 'grid-cols-12',
  };
  const smColsMap: Record<number, string> = {
    1: 'sm:grid-cols-1', 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-3', 4: 'sm:grid-cols-4',
    5: 'sm:grid-cols-5', 6: 'sm:grid-cols-6', 12: 'sm:grid-cols-12',
  };
  const mdColsMap: Record<number, string> = {
    1: 'md:grid-cols-1', 2: 'md:grid-cols-2', 3: 'md:grid-cols-3', 4: 'md:grid-cols-4',
    5: 'md:grid-cols-5', 6: 'md:grid-cols-6', 12: 'md:grid-cols-12',
  };
  const lgColsMap: Record<number, string> = {
    1: 'lg:grid-cols-1', 2: 'lg:grid-cols-2', 3: 'lg:grid-cols-3', 4: 'lg:grid-cols-4',
    5: 'lg:grid-cols-5', 6: 'lg:grid-cols-6', 12: 'lg:grid-cols-12',
  };
  const xlColsMap: Record<number, string> = {
    1: 'xl:grid-cols-1', 2: 'xl:grid-cols-2', 3: 'xl:grid-cols-3', 4: 'xl:grid-cols-4',
    5: 'xl:grid-cols-5', 6: 'xl:grid-cols-6', 12: 'xl:grid-cols-12',
  };

  const gridColsClasses = [
    cols.default && defaultColsMap[cols.default],
    cols.sm && smColsMap[cols.sm],
    cols.md && mdColsMap[cols.md],
    cols.lg && lgColsMap[cols.lg],
    cols.xl && xlColsMap[cols.xl],
  ].filter(Boolean) as string[];

  return (
    <div 
      className={cn(
        'grid',
        ...gridColsClasses,
        gapClasses[gap],
        alignClasses[align],
        className
      )}
    >
      {children}
    </div>
  );
};

interface GridItemProps {
  children: React.ReactNode;
  className?: string;
  span?: {
    default?: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
  };
}

export const GridItem: React.FC<GridItemProps> = ({ 
  children, 
  className,
  span
}) => {
  const spanClasses = [];
  
  if (span?.default) {
    spanClasses.push(`col-span-${span.default}`);
  }
  if (span?.sm) {
    spanClasses.push(`sm:col-span-${span.sm}`);
  }
  if (span?.md) {
    spanClasses.push(`md:col-span-${span.md}`);
  }
  if (span?.lg) {
    spanClasses.push(`lg:col-span-${span.lg}`);
  }
  if (span?.xl) {
    spanClasses.push(`xl:col-span-${span.xl}`);
  }

  return (
    <div 
      className={cn(
        ...spanClasses,
        className
      )}
    >
      {children}
    </div>
  );
};